import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import {ApiOrganization, ApiUserProfile, updateOrganization} from 'src/lib/api/auth';
import {fetchOrganization} from 'src/lib/api/auth';

/**
 * Returns all Organizations for the given user profile.
 */
export default function useOrganizationQuery(
  userProfile: ApiUserProfile | undefined
): UseQueryResult<ApiOrganization[]> {
  // An org that refuses this network would refuse its fetch too, and one failed fetch fails the
  // whole list. The profile still names it, for the page that explains why.
  const organizationIds = userProfile?.organizations
    .filter((o) => !o.networkRestricted)
    .map((o) => o.id);
  const queryKey = organizationIds ? ['organizations', ...organizationIds] : ['organizations'];
  return useQuery(queryKey, async () => {
    if (!organizationIds?.length) {
      return [];
    }
    return Promise.all(organizationIds.map((id) => fetchOrganization(id)));
  });
}

export type UpdatableOrganizationFields = Partial<Pick<ApiOrganization, 'name' | 'settings'>>;

export function useOrganizationUpdateMutation(
  organization: ApiOrganization
): UseMutationResult<ApiOrganization, Error, UpdatableOrganizationFields, () => void> {
  const queryClient = useQueryClient();

  return useMutation(
    (orgUpdateData: UpdatableOrganizationFields) => {
      return updateOrganization(organization.id, orgUpdateData);
    },
    {
      onSuccess: (data: ApiOrganization) => {
        // The updated org may be a child org the user has switched into, so
        // update childOrganizations entries too.
        const applyUpdate = (org: ApiOrganization): ApiOrganization => {
          if (org.id === data.id) return data;
          if (!org.childOrganizations?.some((child) => child.id === data.id)) return org;
          return {
            ...org,
            childOrganizations: org.childOrganizations.map((child) =>
              child.id === data.id ? data : child
            ),
          };
        };
        queryClient.setQueriesData<ApiOrganization[] | undefined>('organizations', (orgs) =>
          orgs?.map(applyUpdate)
        );
        // When operating under a child org this refetch can fail: the backend
        // scopes org lookups to the x-context-organization-id header, which
        // points at the child org by now, so fetching the parent's orgs 404s.
        // fetchOrganization throws in that case, so the query keeps the data
        // written above rather than clobbering it.
        queryClient.invalidateQueries('organizations');
      },
    }
  );
}
